import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { Button } from "../common";
import {
  listPricing,
  createPricing,
  deletePricing as deletePricingApi,
  type PodcastPricing,
  type PricingInterval,
} from "../../lib/pricing-api";
import styles from "./PricingManagement.module.css";

interface PricingManagementProps {
  organizationId: string;
  podcastId: string;
}

const INTERVAL_LABELS: Record<PricingInterval, string> = {
  monthly: "Monthly",
  annual: "Annual",
  lifetime: "Lifetime",
};

const PERIOD_LABELS: Record<PricingInterval, string> = {
  monthly: "/mo",
  annual: "/yr",
  lifetime: "one-time",
};

function formatDollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function PricingManagement({ organizationId, podcastId }: PricingManagementProps) {
  const [tiers, setTiers] = useState<PodcastPricing[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [interval, setInterval] = useState<PricingInterval>("monthly");
  const [priceInput, setPriceInput] = useState("");
  const [trialDaysInput, setTrialDaysInput] = useState("");
  const [validationError, setValidationError] = useState("");

  const fetchTiers = useCallback(async () => {
    try {
      const data = await listPricing(organizationId, podcastId);
      setTiers(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load pricing");
    } finally {
      setLoading(false);
    }
  }, [organizationId, podcastId]);

  useEffect(() => {
    fetchTiers();
  }, [fetchTiers]);

  const usedIntervals = new Set(tiers.map((t) => t.interval));

  const availableIntervals = (["monthly", "annual", "lifetime"] as PricingInterval[]).filter(
    (i) => !usedIntervals.has(i),
  );

  useEffect(() => {
    if (availableIntervals.length > 0 && !availableIntervals.includes(interval)) {
      setInterval(availableIntervals[0]!);
    }
  }, [availableIntervals, interval]);

  function validate(newInterval: PricingInterval, priceCents: number): string | null {
    if (priceCents <= 0) return "Price must be greater than zero";

    if (newInterval === "annual") {
      const monthly = tiers.find((t) => t.interval === "monthly");
      if (monthly && priceCents >= monthly.priceCents * 12) {
        return `Annual price should be less than 12× monthly (${formatDollars(monthly.priceCents * 12)})`;
      }
    }

    if (newInterval === "monthly") {
      const annual = tiers.find((t) => t.interval === "annual");
      if (annual && annual.priceCents >= priceCents * 12) {
        return `Monthly price would make annual (${formatDollars(annual.priceCents)}) not a discount. Consider adjusting.`;
      }
    }

    return null;
  }

  async function handleAdd() {
    const priceCents = Math.round(parseFloat(priceInput) * 100);
    if (isNaN(priceCents) || priceCents <= 0) {
      setValidationError("Enter a valid price");
      return;
    }

    const error = validate(interval, priceCents);
    if (error) {
      setValidationError(error);
      return;
    }

    setValidationError("");
    setSubmitting(true);

    try {
      const trialDays = trialDaysInput ? parseInt(trialDaysInput, 10) : undefined;
      const created = await createPricing(organizationId, podcastId, {
        interval,
        priceCents,
        trialDays: trialDays && trialDays > 0 ? trialDays : undefined,
      });
      setTiers((prev) => [...prev, created]);
      setPriceInput("");
      setTrialDaysInput("");
      toast.success(`${INTERVAL_LABELS[interval]} pricing added`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create pricing");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id: string) {
    setDeletingId(id);
    try {
      await deletePricingApi(organizationId, podcastId, id);
      setTiers((prev) => prev.filter((t) => t.id !== id));
      toast.success("Pricing tier removed");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete pricing");
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  }

  if (loading) return null;

  return (
    <div className={styles.container}>
      <h3>Pricing Tiers</h3>

      {tiers.length === 0 && (
        <p className={styles.emptyState}>
          No pricing tiers configured yet. Add one below to start accepting subscribers.
        </p>
      )}

      <div className={styles.tierList}>
        {tiers.map((tier) => (
          <div key={tier.id} className={styles.tierCard}>
            <div className={styles.tierInfo}>
              <span className={styles.tierInterval}>{INTERVAL_LABELS[tier.interval]}</span>
              <span className={styles.tierPrice}>{formatDollars(tier.priceCents)}</span>
              {tier.trialDays > 0 && (
                <span className={styles.tierTrial}>{tier.trialDays}-day free trial</span>
              )}
            </div>
            <div className={styles.tierActions}>
              {confirmDeleteId === tier.id ? (
                <div className={styles.confirmOverlay}>
                  <span>Delete?</span>
                  <button
                    className={styles.confirmYes}
                    onClick={() => handleDelete(tier.id)}
                    disabled={deletingId === tier.id}
                  >
                    {deletingId === tier.id ? "…" : "Yes"}
                  </button>
                  <button className={styles.confirmNo} onClick={() => setConfirmDeleteId(null)}>
                    No
                  </button>
                </div>
              ) : (
                <button className={styles.deleteBtn} onClick={() => setConfirmDeleteId(tier.id)}>
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {availableIntervals.length > 0 && (
        <div className={styles.addForm}>
          <h4>Add Pricing Tier</h4>
          <div className={styles.formRow}>
            <div className={styles.field}>
              <label htmlFor="pricing-interval">Interval</label>
              <select
                id="pricing-interval"
                value={interval}
                onChange={(e) => setInterval(e.target.value as PricingInterval)}
              >
                {availableIntervals.map((i) => (
                  <option key={i} value={i}>
                    {INTERVAL_LABELS[i]}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label htmlFor="pricing-price">Price (USD)</label>
              <input
                id="pricing-price"
                type="number"
                min="0.50"
                step="0.01"
                placeholder="9.99"
                value={priceInput}
                onChange={(e) => {
                  setPriceInput(e.target.value);
                  setValidationError("");
                }}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="pricing-trial">Trial Days</label>
              <input
                id="pricing-trial"
                type="number"
                min="0"
                max="365"
                placeholder="0"
                value={trialDaysInput}
                onChange={(e) => setTrialDaysInput(e.target.value)}
              />
            </div>
          </div>
          {validationError && <p className={styles.validationError}>{validationError}</p>}
          <div className={styles.addActions}>
            <Button
              type="button"
              variant="primary"
              onClick={handleAdd}
              loading={submitting}
              loadingText="Adding..."
            >
              Add Tier
            </Button>
          </div>
        </div>
      )}

      {tiers.length > 0 && (
        <div className={styles.preview}>
          <h4>Listener Preview</h4>
          <div className={styles.previewCards}>
            {tiers.map((tier) => (
              <div key={tier.id} className={styles.previewCard}>
                <div className={styles.previewInterval}>{INTERVAL_LABELS[tier.interval]}</div>
                <div className={styles.previewPrice}>
                  {formatDollars(tier.priceCents)}
                  <span className={styles.previewPeriod}>{PERIOD_LABELS[tier.interval]}</span>
                </div>
                {tier.trialDays > 0 && (
                  <div className={styles.previewTrial}>{tier.trialDays}-day free trial</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
